import { AEOLIAN, DORIAN } from '../track-kit';
import type { MusicNotes, MusicPart, MusicTrack } from '../types';
import { hitsAt, singles } from './track-kit';

function haloParts(
  degrees: readonly (readonly [degree: number, gain: number])[],
  octave: number,
  followsChord: boolean,
): MusicPart[] {
  return degrees.map(([degree, gain]) => ({
    voice: 'halo',
    layer: 'texture',
    loopSteps: 32,
    octave,
    followsChord,
    notes: [[0, degree, 32]],
    gain,
  }));
}

const AMBYSS_WIND: MusicPart = {
  voice: 'wind',
  layer: 'texture',
  loopSteps: 64,
  octave: 0,
  followsChord: false,
  notes: [
    [0, 0, 40],
    [40, 0, 24],
  ],
};

const AMBYSS_ECHO = { feedback: 0.52, ret: 0.5, tone: 2400, wide: true } as const;

const VOILE_BANSURI: MusicNotes = [
  [0, 3, 1],
  [1, 4, 9],
  [10, 5, 1],
  [11, 4, 1],
  [12, 5, 1],
  [13, 4, 7],
  [20, 6, 2],
  [22, 5, 2],
  [24, 4, 8],
  [32, 4, 6],
  [38, 5, 2],
  [40, 7, 6],
  [46, 6, 2],
  [48, 5, 4],
  [52, 4, 2],
  [54, 3, 2],
  [56, 4, 8],
  [64, 7, 1],
  [65, 8, 7],
  [72, 7, 2],
  [74, 6, 2],
  [76, 7, 4],
  [80, 6, 4],
  [84, 5, 4],
  [88, 4, 8],
  [96, 3, 6],
  [102, 4, 2],
  [104, 5, 6],
  [110, 4, 2],
  [112, 3, 2],
  [114, 1, 2],
  [116, 0, 4],
  [120, 1, 8],
];

const SOUS_LA_VOILE: MusicTrack = {
  id: 'sous-la-voile',
  name: 'Sous la voile',
  rootMidi: 24,
  scale: DORIAN,
  chords: [0, 0, 4, 4, 0, 0, 3, 3],
  kick: { fromHz: 150, release: 0.26 },
  echo: AMBYSS_ECHO,
  padOpens: 220,
  build: 'soft',
  drums: [
    {
      voice: 'kick',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [0, 0.6],
        [16, 0.5],
      ],
      in: 'rise',
    },
    {
      voice: 'rim',
      layer: 'kick',
      loopSteps: 32,
      hits: hitsAt([8, 24], 0.8),
      in: 'rise',
      pan: 0.15,
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [6, 0.5],
        [22, 0.6],
        [23, 0.4],
      ],
      hz: 196,
      pan: -0.5,
      in: 'rise',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [14, 0.5],
        [30, 0.5],
      ],
      hz: 131,
      pan: 0.5,
      in: 'rise',
    },
    {
      voice: 'shaker',
      layer: 'kick',
      loopSteps: 4,
      hits: [
        [0, 0.4],
        [2, 1],
        [3, 0.4],
      ],
      in: 'rise',
      pan: -0.6,
    },
    {
      voice: 'kick',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [0, 0.7],
        [16, 0.62],
        [22, 0.38],
      ],
      in: 'drop',
    },
    { voice: 'doum', layer: 'kick', loopSteps: 32, hits: hitsAt([0, 16], 0.45), in: 'drop' },
    { voice: 'snare', layer: 'kick', loopSteps: 32, hits: hitsAt([8, 24], 0.85), in: 'drop' },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [6, 0.6],
        [7, 0.4],
        [22, 0.6],
        [23, 0.45],
      ],
      hz: 196,
      pan: -0.55,
      in: 'drop',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [11, 0.5],
        [14, 0.6],
        [27, 0.5],
        [30, 0.6],
      ],
      hz: 147,
      pan: 0.55,
      in: 'drop',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [3, 0.4],
        [19, 0.45],
        [29, 0.4],
      ],
      hz: 262,
      pan: 0.2,
      in: 'drop',
    },
    {
      voice: 'udu',
      layer: 'clap',
      loopSteps: 128,
      hits: [
        [120, 0.7],
        [122, 0.6],
      ],
      hz: 294,
      pan: -0.4,
    },
    {
      voice: 'udu',
      layer: 'clap',
      loopSteps: 128,
      hits: [
        [124, 0.7],
        [125, 0.6],
      ],
      hz: 220,
      pan: 0,
    },
    {
      voice: 'udu',
      layer: 'clap',
      loopSteps: 128,
      hits: [
        [126, 0.8],
        [127, 0.7],
      ],
      hz: 147,
      pan: 0.4,
    },
    {
      voice: 'shaker',
      layer: 'kick',
      loopSteps: 4,
      hits: [
        [0, 0.7],
        [1, 0.4],
        [2, 1],
        [3, 0.5],
      ],
      in: 'drop',
      pan: -0.6,
    },
    {
      voice: 'cabasa',
      layer: 'kick',
      loopSteps: 4,
      hits: [
        [0, 0.5],
        [2, 0.9],
      ],
      in: 'rise',
      pan: 0.6,
    },
    {
      voice: 'cabasa',
      layer: 'kick',
      loopSteps: 8,
      hits: [
        [0, 0.6],
        [1, 0.3],
        [2, 1],
        [3, 0.4],
        [4, 0.6],
        [5, 0.35],
        [6, 1],
        [7, 0.6],
      ],
      in: 'drop',
      pan: 0.6,
    },
    { voice: 'hat-open', layer: 'clap', loopSteps: 8, hits: [[4, 1]], pan: 0.6 },
    { voice: 'rattle', layer: 'clap', loopSteps: 16, hits: hitsAt([6, 14], 0.9), pan: 0.7 },
    { voice: 'tek', layer: 'clap', loopSteps: 32, hits: hitsAt([3, 13, 21, 27], 0.5), pan: -0.7 },
  ],
  parts: [
    {
      voice: 'glide-sub',
      layer: 'bass',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [
        [0, 0, 22],
        [24, 4, 6],
        [30, 0, 2],
      ],
      accents: [0],
      gain: 0.62,
    },
    {
      voice: 'sub',
      layer: 'texture',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [[0, 0, 32]],
      in: 'light',
      gain: 0.5,
    },
    ...haloParts(
      [
        [0, 1.7],
        [4, 1.4],
        [9, 0.9],
      ],
      2,
      true,
    ),
    ...haloParts(
      [
        [0, 0.7],
        [2, 0.5],
        [4, 0.6],
        [6, 0.4],
      ],
      3,
      true,
    ),
    { ...AMBYSS_WIND, gain: 2.2 },
    {
      voice: 'mallet',
      layer: 'arp',
      loopSteps: 64,
      octave: 3,
      followsChord: true,
      notes: singles([
        [0, 0],
        [3, 4],
        [6, 7],
        [10, 6],
        [12, 4],
        [16, 2],
        [19, 4],
        [22, 7],
        [26, 9],
        [28, 7],
        [32, 0],
        [35, 4],
        [38, 7],
        [42, 8],
        [44, 7],
        [48, 6],
        [51, 4],
        [54, 2],
        [58, 4],
        [60, 1],
      ]),
      in: 'full',
      gain: 1.6,
    },
    {
      voice: 'mallet',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: true,
      notes: singles([
        [0, 4],
        [6, 7],
        [14, 9],
        [22, 6],
      ]),
      in: 'light',
    },
    {
      voice: 'bubble',
      layer: 'squelch',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: singles([
        [2, 0],
        [5, 4],
        [7, 7],
        [10, 0],
        [13, 4],
        [15, 6],
      ]),
      gain: 0.8,
    },
    {
      voice: 'bird',
      layer: 'texture',
      loopSteps: 128,
      octave: 0,
      followsChord: false,
      notes: singles([
        [21, 0],
        [70, 1],
        [109, 2],
      ]),
    },
    {
      voice: 'bansuri',
      layer: 'lead',
      loopSteps: 128,
      octave: 3,
      followsChord: false,
      notes: [
        [0, 3, 1],
        [1, 4, 13],
        [40, 7, 6],
        [46, 6, 2],
        [48, 4, 10],
        [96, 3, 6],
        [102, 4, 2],
        [104, 5, 8],
      ],
      slides: [1, 46, 48, 102, 104],
    },
    {
      voice: 'bansuri',
      layer: 'theme',
      loopSteps: 128,
      octave: 3,
      followsChord: false,
      notes: VOILE_BANSURI,
      gain: 1.4,
      slides: [
        1, 10, 11, 12, 13, 22, 24, 38, 46, 48, 52, 54, 56, 65, 72, 74, 76, 84, 88, 102, 104, 110,
        112, 114, 116,
      ],
    },
    ...[0, 2, 4].map((degree): MusicPart => ({
      voice: 'ooh',
      layer: 'clap',
      loopSteps: 32,
      octave: 2,
      followsChord: true,
      notes: [[0, degree, 32]],
      gain: 0.7,
    })),
  ],
};

const LES_QUATRE_MARCHES: MusicTrack = {
  id: 'les-quatre-marches',
  name: 'Les quatre marches',
  rootMidi: 33,
  scale: AEOLIAN,
  chords: [0, 0, -1, -1, -2, -2, -3, -3],
  kick: { fromHz: 120, release: 0.32 },
  echo: { ...AMBYSS_ECHO, feedback: 0.58 },
  padOpens: 260,
  build: 'soft',
  drums: [
    {
      voice: 'tom',
      layer: 'kick',
      loopSteps: 16,
      hits: [[0, 0.6]],
      hz: 96,
      release: 0.5,
      in: 'rise',
    },
    {
      voice: 'tom',
      layer: 'kick',
      loopSteps: 16,
      hits: [[3, 0.3]],
      hz: 80,
      release: 0.4,
      in: 'rise',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [10, 0.4],
        [22, 0.45],
        [28, 0.35],
      ],
      hz: 165,
      pan: -0.5,
      in: 'rise',
    },
    {
      voice: 'kick',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [0, 0.62],
        [16, 0.5],
      ],
      in: 'drop',
    },
    {
      voice: 'tom',
      layer: 'kick',
      loopSteps: 16,
      hits: [[3, 0.3]],
      hz: 80,
      release: 0.4,
      in: 'drop',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [10, 0.5],
        [11, 0.3],
        [22, 0.5],
        [28, 0.4],
      ],
      hz: 165,
      pan: -0.5,
      in: 'drop',
    },
    {
      voice: 'udu',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [6, 0.4],
        [14, 0.5],
        [30, 0.5],
      ],
      hz: 220,
      pan: 0.5,
      in: 'drop',
    },
    {
      voice: 'tek',
      layer: 'kick',
      loopSteps: 32,
      hits: [
        [8, 0.6],
        [24, 0.7],
      ],
      pan: 0.25,
      in: 'drop',
    },
    {
      voice: 'rattle',
      layer: 'clap',
      loopSteps: 8,
      hits: [
        [2, 0.5],
        [6, 0.9],
      ],
      pan: 0.6,
    },
    {
      voice: 'shaker',
      layer: 'clap',
      loopSteps: 4,
      hits: [
        [0, 0.3],
        [2, 0.7],
      ],
      pan: -0.6,
    },
  ],
  parts: [
    {
      voice: 'halo',
      layer: 'bass',
      loopSteps: 32,
      octave: 2,
      followsChord: false,
      notes: [[0, 0, 32]],
      in: 'rise',
      gain: 2.2,
    },
    {
      voice: 'halo',
      layer: 'bass',
      loopSteps: 32,
      octave: 2,
      followsChord: false,
      notes: [[0, 4, 32]],
      in: 'rise',
      gain: 2.2,
    },
    {
      voice: 'sub',
      layer: 'bass',
      loopSteps: 16,
      octave: 0,
      followsChord: true,
      notes: [[0, 0, 16]],
      gain: 0.34,
    },
    {
      voice: 'drone',
      layer: 'bass',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [[0, 0, 32]],
      gain: 1.6,
    },
    {
      voice: 'sub-saw',
      layer: 'bass',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [
        [0, 0, 26],
        [26, 4, 6],
      ],
      gain: 0.7,
    },
    {
      voice: 'drone',
      layer: 'texture',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [[0, 0, 32]],
      in: 'light',
      gain: 1,
    },
    ...haloParts(
      [
        [0, 0.9],
        [3, 0.85],
        [4, 1],
      ],
      2,
      false,
    ),
    {
      voice: 'halo',
      layer: 'texture',
      loopSteps: 32,
      octave: 2,
      followsChord: true,
      notes: [[0, 9, 32]],
      gain: 0.55,
    },
    AMBYSS_WIND,
    {
      voice: 'felt',
      layer: 'arp',
      loopSteps: 32,
      octave: 2,
      followsChord: true,
      notes: singles([
        [0, 0],
        [6, 4],
        [12, 7],
        [16, 9],
        [22, 7],
        [28, 4],
      ]),
      gain: 1.1,
    },
    {
      voice: 'mallet',
      layer: 'squelch',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: singles([
        [2, 4],
        [10, 7],
        [20, 6],
        [34, 3],
        [42, 4],
        [52, 8],
      ]),
      gain: 0.8,
    },
    {
      voice: 'mallet',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: false,
      notes: singles([
        [4, 4],
        [20, 3],
      ]),
      in: 'light',
      gain: 0.9,
    },
    {
      voice: 'bird',
      layer: 'texture',
      loopSteps: 64,
      octave: 0,
      followsChord: false,
      notes: singles([
        [13, 0],
        [45, 3],
      ]),
      in: 'light',
    },
    {
      voice: 'cello',
      layer: 'lead',
      loopSteps: 128,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 4, 20],
        [32, 3, 20],
        [64, 2, 12],
        [76, 3, 12],
        [96, 4, 24],
      ],
    },
    ...(['cello', 'ooh'] as const).map((voice): MusicPart => ({
      voice,
      layer: 'theme',
      loopSteps: 128,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 4, 24],
        [24, 6, 8],
        [32, 3, 24],
        [56, 1, 8],
        [64, 2, 12],
        [76, 3, 12],
        [88, 4, 8],
        [96, 4, 16],
        [112, 3, 8],
        [120, 1, 8],
      ],
      gain: voice === 'cello' ? 1.2 : 1.6,
    })),
    ...[0, 4].map((degree): MusicPart => ({
      voice: 'ooh',
      layer: 'clap',
      loopSteps: 32,
      octave: 1,
      followsChord: true,
      notes: [[0, degree, 32]],
      gain: 1.1,
    })),
  ],
};

export const AMBYSS_TRACKS: readonly MusicTrack[] = [SOUS_LA_VOILE, LES_QUATRE_MARCHES];
